// ============================================================================
// props.js — the low-poly procedural set-dressing kit. Every arena is built from
// these: flat-shaded primitives baked (merged, vertex-coloured) into single
// geometries so a whole forest or field of rocks is ONE instanced draw call, plus
// landmark builders (windmill, lighthouse, watchtower, sunken keep, bridge, …) and
// terrain framing (cliffs, hills, water). On-brand with the procedural crowd; no
// textures needed. Scenery is built once at level load (may use Math.random — it
// never runs inside the deterministic sim tick).
// ============================================================================

import * as THREE from 'three';

// ---- shared flat-shaded materials, cached by colour+flags --------------------
const _mats = new Map();
export function mat(hex, o = {}) {
  const key = hex + '|' + (o.emissive || 0) + '|' + (o.ei || 0) + '|' + (o.op != null ? o.op : 1) + '|' + (o.side || 0) + '|' + (o.flat === false ? 0 : 1);
  let m = _mats.get(key);
  if (m) return m;
  m = new THREE.MeshLambertMaterial({
    color: hex, flatShading: o.flat !== false,
    emissive: o.emissive || 0x000000, emissiveIntensity: o.ei || 0,
    transparent: o.op != null && o.op < 1, opacity: o.op != null ? o.op : 1,
    side: o.side || THREE.FrontSide, depthWrite: o.op != null && o.op < 1 ? false : true,
  });
  _mats.set(key, m);
  return m;
}

// ---- bake: merge a list of coloured primitive parts into one vertex-coloured geo
// part = { geo, color, pos:[x,y,z], rot:[x,y,z], scale:[x,y,z]|n }
const _mtmp = new THREE.Matrix4(), _qtmp = new THREE.Quaternion(), _etmp = new THREE.Euler(), _ptmp = new THREE.Vector3(), _stmp = new THREE.Vector3();
export function bake(parts) {
  const geos = [];
  for (const p of parts) {
    let g = p.geo.index ? p.geo.toNonIndexed() : p.geo.clone();
    if (g.attributes.uv) g.deleteAttribute('uv');
    const pos = p.pos || [0, 0, 0], rot = p.rot || [0, 0, 0];
    const s = p.scale == null ? 1 : p.scale;
    _ptmp.set(pos[0], pos[1], pos[2]);
    _qtmp.setFromEuler(_etmp.set(rot[0], rot[1], rot[2]));
    _stmp.set(Array.isArray(s) ? s[0] : s, Array.isArray(s) ? s[1] : s, Array.isArray(s) ? s[2] : s);
    g.applyMatrix4(_mtmp.compose(_ptmp, _qtmp, _stmp));
    if (!g.attributes.normal) g.computeVertexNormals();
    const n = g.attributes.position.count, col = new Float32Array(n * 3), c = new THREE.Color(p.color);
    for (let i = 0; i < n; i++) { col[i * 3] = c.r; col[i * 3 + 1] = c.g; col[i * 3 + 2] = c.b; }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geos.push(g);
  }
  let total = 0; for (const g of geos) total += g.attributes.position.count;
  const P = new Float32Array(total * 3), N = new Float32Array(total * 3), C = new Float32Array(total * 3);
  let o = 0;
  for (const g of geos) {
    const gp = g.attributes.position.array, gn = g.attributes.normal.array, gc = g.attributes.color.array, cnt = g.attributes.position.count;
    P.set(gp, o * 3); N.set(gn, o * 3); C.set(gc, o * 3); o += cnt;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(P, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(N, 3));
  out.setAttribute('color', new THREE.BufferAttribute(C, 3));
  out.computeBoundingSphere();
  return out;
}
const VCOL = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });

// place a baked geometry as an InstancedMesh across many transforms (one draw call)
export function scatter(group, geo, placements, material) {
  if (!placements.length) return null;
  const inst = new THREE.InstancedMesh(geo, material || VCOL, placements.length);
  inst.frustumCulled = true;
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), p = new THREE.Vector3(), s = new THREE.Vector3();
  for (let i = 0; i < placements.length; i++) {
    const t = placements[i];
    p.set(t.x, t.y || 0, t.z);
    q.setFromEuler(e.set(0, t.ry || 0, 0));
    const sc = t.s || 1; s.set(sc, (t.sy || sc), sc);
    inst.setMatrixAt(i, m.compose(p, q, s));
  }
  inst.instanceMatrix.needsUpdate = true;
  group.add(inst);
  return inst;
}

// ============================================================================
// SCATTER GEO — merged geometries meant to be instanced hundreds of times.
// Colours can be tinted per arena via the `c` (palette) argument.
// ============================================================================
const cone = (rt, rb, h, seg) => new THREE.ConeGeometry(rt != null ? rt : 0, rb, seg || 6, 1); // (top r ignored by cone; use rb=radius)
const cyl = (rt, rb, h, seg) => new THREE.CylinderGeometry(rt, rb, h, seg || 6);
const box = (w, h, d) => new THREE.BoxGeometry(w, h, d);
const ico = (r, d) => new THREE.IcosahedronGeometry(r, d || 0);

export const SCATTER = {
  tree(c = {}) {
    const bark = c.bark || 0x4b3b2a, leaf = c.leaf || 0x5a6b3a;
    return bake([
      { geo: cyl(0.28, 0.42, 3.2, 5), color: bark, pos: [0, 1.6, 0] },
      { geo: ico(2.1, 0), color: leaf, pos: [0, 3.9, 0], scale: [1, 0.85, 1] },
      { geo: ico(1.5, 0), color: leaf, pos: [0.8, 3.2, 0.5], scale: 0.8 },
    ]);
  },
  pine(c = {}) {
    const bark = c.bark || 0x3a2e22, leaf = c.leaf || 0x2f4a30;
    return bake([
      { geo: cyl(0.18, 0.3, 2.6, 5), color: bark, pos: [0, 1.3, 0] },
      { geo: new THREE.ConeGeometry(1.7, 3.0, 6), color: leaf, pos: [0, 3.2, 0] },
      { geo: new THREE.ConeGeometry(1.3, 2.4, 6), color: leaf, pos: [0, 4.5, 0] },
      { geo: new THREE.ConeGeometry(0.85, 1.8, 6), color: leaf, pos: [0, 5.7, 0] },
    ]);
  },
  deadTree(c = {}) {
    const bark = c.bark || 0x3a3230;
    return bake([
      { geo: cyl(0.16, 0.4, 4.2, 5), color: bark, pos: [0, 2.1, 0] },
      { geo: cyl(0.08, 0.16, 1.8, 4), color: bark, pos: [0.7, 3.4, 0], rot: [0, 0, -0.7] },
      { geo: cyl(0.08, 0.16, 1.6, 4), color: bark, pos: [-0.6, 3.0, 0.3], rot: [0, 0, 0.8] },
      { geo: cyl(0.06, 0.12, 1.3, 4), color: bark, pos: [0.2, 4.4, 0.5], rot: [0.6, 0, 0.3] },
    ]);
  },
  bush(c = {}) { return bake([{ geo: ico(1.0, 0), color: c.leaf || 0x4a5a34, scale: [1, 0.7, 1] }]); },
  reed(c = {}) {
    const col = c.reed || 0x7a7a48; const parts = [];
    for (let i = 0; i < 5; i++) { const a = i / 5 * 6.28; parts.push({ geo: cyl(0.03, 0.06, 1.6 + Math.random() * 0.8, 3), color: col, pos: [Math.cos(a) * 0.25, 0.9, Math.sin(a) * 0.25], rot: [Math.cos(a) * 0.2, 0, Math.sin(a) * 0.2] }); }
    return bake(parts);
  },
  wheat(c = {}) {
    const stalk = c.stalk || 0xb89a4a, head = c.head || 0xd8b95a; const parts = [];
    for (let i = 0; i < 6; i++) { const a = Math.random() * 6.28, r = Math.random() * 0.3; parts.push({ geo: cyl(0.02, 0.03, 1.1, 3), color: stalk, pos: [Math.cos(a) * r, 0.55, Math.sin(a) * r] }); parts.push({ geo: ico(0.12, 0), color: head, pos: [Math.cos(a) * r, 1.15, Math.sin(a) * r], scale: [0.6, 1.6, 0.6] }); }
    return bake(parts);
  },
  rock(c = {}) { return bake([{ geo: ico(1.0, 0), color: c.rock || 0x6b6660, scale: [1.2, 0.7, 1.0] }]); },
  boulder(c = {}) {
    return bake([
      { geo: ico(2.4, 0), color: c.rock || 0x5f5a54, scale: [1.2, 0.9, 1.1] },
      { geo: ico(1.3, 0), color: c.rock || 0x6b665f, pos: [1.6, 0.4, 0.6], scale: [1, 0.8, 1] },
    ]);
  },
  standingStone(c = {}) { return bake([{ geo: box(1.4, 6.5, 1.0), color: c.rock || 0x585652, rot: [0.04, 0.5, 0.06] }]); },
  iceSpike(c = {}) {
    const ice = c.ice || 0xbcd6e6;
    return bake([
      { geo: new THREE.ConeGeometry(0.9, 5.5, 5), color: ice, pos: [0, 2.7, 0], rot: [0.05, 0, 0.05] },
      { geo: new THREE.ConeGeometry(0.5, 3.0, 5), color: ice, pos: [1.0, 1.5, 0.4] },
    ]);
  },
  obsidianSpire(c = {}) {
    const ob = c.rock || 0x1a1416, glow = c.accent || 0xff5210;
    return bake([
      { geo: new THREE.ConeGeometry(1.6, 9, 5), color: ob, pos: [0, 4.5, 0], rot: [0.06, 0.3, 0.05] },
      { geo: new THREE.ConeGeometry(0.7, 4, 5), color: glow, pos: [1.1, 2, 0.5] },
    ]);
  },
  crate(c = {}) { return bake([{ geo: box(1.1, 1.1, 1.1), color: c.wood || 0x6a5236 }]); },
  barrel(c = {}) { return bake([{ geo: cyl(0.55, 0.55, 1.2, 8), color: c.wood || 0x5a4326 }]); },
  hayBale(c = {}) { return bake([{ geo: cyl(0.9, 0.9, 1.5, 8), color: c.hay || 0xc7a94e, rot: [0, 0, 1.5708] }]); },
  corpsePile(c = {}) {
    const d = c.dark || 0x2a2320; return bake([
      { geo: ico(1.3, 0), color: d, scale: [1.6, 0.5, 1.2] },
      { geo: box(0.2, 0.2, 1.6), color: 0x6b6058, pos: [0.5, 0.4, 0.2], rot: [0, 0.6, 1.2] },
    ]);
  },
  grave(c = {}) { return bake([{ geo: box(0.7, 1.1, 0.16), color: c.rock || 0x555049, pos: [0, 0.55, 0], rot: [0.08, 0, 0.05] }]); },
};

// ============================================================================
// LANDMARKS — individual set-pieces. builder(ctx, x, z, p) adds to ctx.group and
// may register a light (ctx.light) or a per-frame updater (ctx.anim). Returns void.
// ctx = { group, palette, biome, light(color,intensity,dist,x,y,z), anim(fn), struct(a themed stone material) }
// ============================================================================
function add(ctx, parts, x, z) { const g = new THREE.Mesh(bake(parts), VCOL); g.position.set(x, 0, z); ctx.group.add(g); return g; }

export const LANDMARK = {
  // ---- structures ----
  watchtower(ctx, x, z, p = {}) {
    const st = p.stone || ctx.palette.stone || 0x8a827a, wood = 0x4a3a28;
    const h = p.h || 12;
    add(ctx, [
      { geo: box(4, h, 4), color: st, pos: [0, h / 2, 0] },
      { geo: box(5.2, 1.2, 5.2), color: st, pos: [0, h, 0] },
      { geo: box(5.2, 2.6, 0.4), color: st, pos: [0, h + 1.3, 2.4] },
      { geo: box(5.2, 2.6, 0.4), color: st, pos: [0, h + 1.3, -2.4] },
      { geo: new THREE.ConeGeometry(3.6, 3, 4), color: wood, pos: [0, h + 3, 0], rot: [0, 0.785, 0] },
    ], x, z);
    if (p.solid !== false && ctx.solid) ctx.solid.circle(x, z, 3.2);
  },
  ruinedWall(ctx, x, z, p = {}) {
    const st = p.stone || ctx.palette.stone || 0x746c62, len = p.len || 22, rot = p.rot || 0;
    const parts = []; const n = Math.max(2, Math.round(len / 5));
    for (let i = 0; i < n; i++) { const hh = 3 + Math.random() * 5; parts.push({ geo: box(5, hh, 2.4), color: st, pos: [(i - (n - 1) / 2) * 5, hh / 2, 0], rot: [0, 0, (Math.random() - 0.5) * 0.06] }); }
    const g = add(ctx, parts, x, z); g.rotation.y = rot; return g;
  },
  fortWall(ctx, x, z, p = {}) {
    const st = p.stone || ctx.palette.stone || 0x6a6258, len = p.len || 60, rot = p.rot || 0, h = p.h || 11;
    const parts = [{ geo: box(len, h, 6), color: st, pos: [0, h / 2, 0] }];
    for (let i = 0; i <= len; i += 4) parts.push({ geo: box(2.4, 2, 6), color: st, pos: [i - len / 2, h + 1, 0] });
    const g = add(ctx, parts, x, z); g.rotation.y = rot;
    if (p.solid !== false && ctx.solid) ctx.solid.box(x, z, len / 2, 3, rot);
    return g;
  },
  gate(ctx, x, z, p = {}) {
    const st = p.stone || ctx.palette.stone || 0x5f584f, rot = p.rot || 0, broken = p.broken;
    const parts = [
      { geo: box(4, 13, 5), color: st, pos: [-7, 6.5, 0] },
      { geo: box(4, 13, 5), color: st, pos: [7, 6.5, 0] },
    ];
    if (!broken) parts.push({ geo: box(20, 4, 5), color: st, pos: [0, 12, 0] });
    else parts.push({ geo: box(9, 3.4, 5), color: st, pos: [-2, 2, 0], rot: [0, 0, 0.4] });
    const g = add(ctx, parts, x, z); g.rotation.y = rot;
    // pillar world offsets under Y-rotation: local (±7,0) → world (±7·cos, ∓7·sin)
    if (p.solid !== false && ctx.solid) { const c = Math.cos(rot), s = Math.sin(rot); ctx.solid.circle(x - 7 * c, z + 7 * s, 2.6); ctx.solid.circle(x + 7 * c, z - 7 * s, 2.6); }
    return g;
  },
  keep(ctx, x, z, p = {}) {
    const st = p.stone || ctx.palette.stone || 0x4a443c, dark = p.dark || 0x2c2824;
    add(ctx, [
      { geo: box(26, 22, 26), color: st, pos: [0, 11, 0] },
      { geo: box(18, 8, 18), color: dark, pos: [0, 26, 0] },
      { geo: new THREE.ConeGeometry(13, 8, 4), color: dark, pos: [0, 34, 0], rot: [0, 0.785, 0] },
      { geo: box(30, 3, 30), color: st, pos: [0, 1.5, 0] },
    ], x, z);
  },
  sunkenKeep(ctx, x, z, p = {}) {
    const st = p.stone || ctx.palette.stone || 0x3e4438, moss = 0x445037;
    add(ctx, [
      { geo: box(20, 16, 20), color: st, pos: [0, 6, 0], rot: [0.06, 0.2, 0.04] },
      { geo: box(6, 12, 6), color: st, pos: [8, 8, -6], rot: [0.1, 0, -0.15] },
      { geo: box(22, 2, 22), color: moss, pos: [0, 0.4, 0], rot: [0.06, 0.2, 0.04] },
    ], x, z);
  },
  windmill(ctx, x, z, p = {}) {
    const st = p.stone || 0x8a7a5a, wood = 0x5a4326;
    const g = new THREE.Mesh(bake([
      { geo: cyl(3.4, 4.4, 12, 8), color: st, pos: [0, 6, 0] },
      { geo: new THREE.ConeGeometry(4.2, 4, 8), color: wood, pos: [0, 13.5, 0] },
    ]), VCOL); g.position.set(x, 0, z); ctx.group.add(g);
    // spinning sails
    const sail = new THREE.Group(); sail.position.set(x, 11, z + 3.6);
    const blade = bake([{ geo: box(0.8, 9, 0.3), color: wood, pos: [0, 4.6, 0] }, { geo: box(2.4, 6, 0.2), color: 0x9a8a5a, pos: [1.4, 4.6, 0.1] }]);
    for (let i = 0; i < 4; i++) { const b = new THREE.Mesh(blade, VCOL); b.rotation.z = i * 1.5708; sail.add(b); }
    ctx.group.add(sail);
    if (p.burning) ctx.light(0xff6a1a, 2.2, 40, x, 10, z);
    ctx.anim((dt) => { sail.rotation.z += dt * (p.burning ? 1.4 : 0.5); });
    if (p.solid !== false && ctx.solid) ctx.solid.circle(x, z, 4.6);
  },
  barn(ctx, x, z, p = {}) {
    const wood = p.wood || 0x6a4a2a, roof = p.roof || 0x7a3a20;
    add(ctx, [
      { geo: box(12, 7, 9), color: wood, pos: [0, 3.5, 0] },
      { geo: new THREE.CylinderGeometry(0.001, 6.4, 5, 4, 1, false, 0.785, 3.14), color: roof, pos: [0, 7, 0], rot: [0, 0.785, 0], scale: [1, 1, 1.42] },
    ], x, z);
    if (p.burning && ctx.fire) ctx.fire(x, z, 2.6);   // the fire brings its own light
  },
  well(ctx, x, z, p = {}) {
    const st = p.stone || 0x6a625a;
    add(ctx, [{ geo: cyl(1.6, 1.8, 2.2, 8), color: st, pos: [0, 1.1, 0] }, { geo: box(0.3, 2.6, 0.3), color: 0x4a3a28, pos: [-1.4, 2.3, 0] }, { geo: box(0.3, 2.6, 0.3), color: 0x4a3a28, pos: [1.4, 2.3, 0] }, { geo: new THREE.ConeGeometry(2.4, 1.4, 4), color: 0x5a3a20, pos: [0, 4.2, 0], rot: [0, 0.785, 0] }], x, z);
    if (p.solid !== false && ctx.solid) ctx.solid.circle(x, z, 2.0);
  },
  fence(ctx, x, z, p = {}) {
    const wood = p.wood || 0x5a4530, len = p.len || 18, rot = p.rot || 0, n = Math.round(len / 3);
    const parts = [];
    for (let i = 0; i <= n; i++) parts.push({ geo: box(0.3, 1.8, 0.3), color: wood, pos: [(i - n / 2) * 3, 0.9, 0] });
    parts.push({ geo: box(len, 0.2, 0.2), color: wood, pos: [0, 1.3, 0] });
    parts.push({ geo: box(len, 0.2, 0.2), color: wood, pos: [0, 0.6, 0] });
    const g = add(ctx, parts, x, z); g.rotation.y = rot; return g;
  },
  cart(ctx, x, z, p = {}) {
    const wood = 0x5a4326;
    const g = add(ctx, [{ geo: box(3.4, 1.2, 2), color: wood, pos: [0, 1.4, 0] }, { geo: cyl(1, 1, 0.3, 8), color: 0x3a2e1e, pos: [-1.2, 0.9, 1.1], rot: [1.5708, 0, 0] }, { geo: cyl(1, 1, 0.3, 8), color: 0x3a2e1e, pos: [-1.2, 0.9, -1.1], rot: [1.5708, 0, 0] }], x, z);
    g.rotation.y = p.rot || Math.random() * 3.14; return g;
  },
  wreckage(ctx, x, z, p = {}) {
    const wood = 0x4a3a2a;
    const g = add(ctx, [{ geo: box(5, 0.5, 1.4), color: wood, pos: [0, 0.4, 0], rot: [0, 0, 0.2] }, { geo: box(0.4, 3, 0.4), color: wood, pos: [1, 1.4, 0.3], rot: [0.3, 0, 0.5] }, { geo: box(0.4, 2.4, 0.4), color: wood, pos: [-1.4, 1, -0.4], rot: [-0.4, 0, -0.3] }], x, z);
    g.rotation.y = p.rot || Math.random() * 6.28; return g;
  },
  tent(ctx, x, z, p = {}) {
    const cloth = p.cloth || 0x6a4a3a;
    const g = add(ctx, [{ geo: new THREE.ConeGeometry(3, 4, 4), color: cloth, pos: [0, 2, 0], rot: [0, 0.785, 0] }, { geo: box(0.2, 4.4, 0.2), color: 0x3a2e1e, pos: [0, 2.2, 0] }], x, z);
    g.rotation.y = p.rot || 0; return g;
  },
  pier(ctx, x, z, p = {}) {
    const wood = p.wood || 0x4a3e2e, len = p.len || 34, rot = p.rot || 0, parts = [{ geo: box(6, 0.6, len), color: wood, pos: [0, 1.4, 0] }];
    for (let i = 0; i <= len; i += 4) { parts.push({ geo: box(0.5, 3, 0.5), color: 0x3a2e1e, pos: [-2.4, 0, i - len / 2] }); parts.push({ geo: box(0.5, 3, 0.5), color: 0x3a2e1e, pos: [2.4, 0, i - len / 2] }); }
    const g = add(ctx, parts, x, z); g.rotation.y = rot; return g;
  },
  boat(ctx, x, z, p = {}) {
    const wood = 0x4a3a28;
    const g = add(ctx, [{ geo: new THREE.CylinderGeometry(1.4, 0.4, 7, 6, 1, false), color: wood, pos: [0, 0.7, 0], rot: [1.5708, 0, 0], scale: [1, 1, 0.5] }, { geo: box(0.2, 5, 0.2), color: 0x3a2e1e, pos: [0, 2.6, 0] }], x, z);
    g.rotation.y = p.rot || Math.random() * 6.28; g.rotation.z = 0.08; return g;
  },
  lighthouse(ctx, x, z, p = {}) {
    const st = p.stone || 0xb0a89a, red = 0x8a2a1a;
    const g = new THREE.Mesh(bake([
      { geo: cyl(2.6, 4.2, 20, 10), color: st, pos: [0, 10, 0] },
      { geo: cyl(2.4, 2.4, 2, 10), color: red, pos: [0, 12, 0] },
      { geo: cyl(2.4, 2.4, 2, 10), color: red, pos: [0, 16, 0] },
      { geo: cyl(3, 3, 2.4, 10), color: 0x2c2824, pos: [0, 21, 0] },
      { geo: new THREE.ConeGeometry(3.2, 3, 10), color: red, pos: [0, 23.6, 0] },
    ]), VCOL); g.position.set(x, 0, z); ctx.group.add(g);
    const lamp = ctx.light(0xffe6a0, 2.6, 120, x, 21, z);
    ctx.anim((dt, t) => { if (lamp) lamp.intensity = 1.6 + Math.abs(Math.sin(t * 1.2)) * 2.4; });
    if (p.solid !== false && ctx.solid) ctx.solid.circle(x, z, 4.4);
  },
  bridge(ctx, x, z, p = {}) {
    const st = p.stone || ctx.palette.stone || 0x6a635a, len = p.len || 60, rot = p.rot || 0, w = p.w || 14;
    const parts = [
      { geo: box(w, 2, len), color: st, pos: [0, 3, 0] },
      { geo: box(1.2, 4, len), color: st, pos: [-w / 2 + 0.6, 5, 0] },
      { geo: box(1.2, 4, len), color: st, pos: [w / 2 - 0.6, 5, 0] },
    ];
    for (const sx of [-1, 1]) parts.push({ geo: cyl(2, 2.6, 6, 6), color: st, pos: [sx * (w / 2 - 1), 0, 0], scale: [1, 1, 2] });
    const g = add(ctx, parts, x, z); g.rotation.y = rot; return g;
  },
  shrine(ctx, x, z, p = {}) {
    const st = p.stone || 0x6a6258, accent = p.accent || ctx.palette.accent || 0x9ab0cc;
    add(ctx, [
      { geo: box(6, 1, 6), color: st, pos: [0, 0.5, 0] },
      { geo: box(0.8, 5, 0.8), color: st, pos: [-2.2, 3, -2.2] }, { geo: box(0.8, 5, 0.8), color: st, pos: [2.2, 3, -2.2] },
      { geo: box(0.8, 5, 0.8), color: st, pos: [-2.2, 3, 2.2] }, { geo: box(0.8, 5, 0.8), color: st, pos: [2.2, 3, 2.2] },
      { geo: new THREE.ConeGeometry(5, 3, 4), color: st, pos: [0, 7, 0], rot: [0, 0.785, 0] },
      { geo: ico(1.1, 0), color: accent, pos: [0, 2, 0] },
    ], x, z);
    if (p.solid !== false && ctx.solid) ctx.solid.circle(x, z, 3.6);
    ctx.light(accent, 1.4, 30, x, 3, z);
  },
  siegeTower(ctx, x, z, p = {}) {
    const wood = 0x4a3826;
    const g = add(ctx, [{ geo: box(7, 16, 7), color: wood, pos: [0, 8, 0] }, { geo: box(8, 1, 4), color: wood, pos: [0, 15, 4] }, { geo: cyl(1, 1, 0.4, 8), color: 0x2a2018, pos: [-3.4, 1, 3], rot: [1.5708, 0, 0] }, { geo: cyl(1, 1, 0.4, 8), color: 0x2a2018, pos: [3.4, 1, 3], rot: [1.5708, 0, 0] }], x, z);
    g.rotation.y = p.rot || 0; return g;
  },
  // ---- signature centrepieces ----
  obelisk(ctx, x, z, p = {}) {                         // colossal leaning cracked obsidian
    const ob = p.stone || 0x14100f, glow = p.accent || ctx.palette.accent || 0xff5210, h = p.h || 30;
    const g = new THREE.Mesh(bake([
      { geo: new THREE.ConeGeometry(3.2, h, 5), color: ob, pos: [0, h / 2, 0] },
      { geo: box(1.2, h * 0.5, 0.5), color: glow, pos: [0.8, h * 0.4, 1.4], rot: [0.1, 0, 0.06] },   // ember fissure
      { geo: box(0.6, h * 0.35, 0.4), color: glow, pos: [-1.1, h * 0.5, 0.9] },
      { geo: ico(4.5, 0), color: ob, pos: [0, 1, 0], scale: [1.3, 0.5, 1.3] },                       // rubble base
    ]), VCOL);
    g.position.set(x, 0, z); g.rotation.set(0.14, p.ry || 0.4, 0.1); ctx.group.add(g);   // the lean
    if (h > 24) ctx.light(glow, 2.2, 62, x, h * 0.4, z);   // only the hero obelisk warrants a light
    if (p.solid !== false && ctx.solid) ctx.solid.circle(x, z, 3.2 + h * 0.03);
  },
  emberVent(ctx, x, z, p = {}) {                        // a glowing crack venting heat (self-lit plane; no scene light — cheap)
    const glow = p.accent || 0xff6a1a;
    const crack = new THREE.Mesh(new THREE.PlaneGeometry(p.w || 5, p.d || 1.6).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: glow }));
    crack.position.set(x, 0.06, z); crack.rotation.z = p.ry || Math.random() * 3.14; ctx.group.add(crack);
  },
  caravan(ctx, x, z, p = {}) {                          // an overturned merchant wreck — a rally island
    const wood = 0x5a4326, beast = 0x2e2620;
    const g = add(ctx, [
      { geo: box(3.6, 1.4, 2.2), color: wood, pos: [0, 1.4, 0], rot: [0, 0, 1.1] },                  // tipped cart
      { geo: cyl(1.1, 1.1, 0.3, 8), color: 0x2a2018, pos: [1.6, 1.2, 1.2], rot: [1.5708, 0, 0] },
      { geo: box(3, 1.1, 1.8), color: wood, pos: [3.4, 0.9, -1], rot: [0, 0.6, 0.2] },               // second cart
      { geo: ico(1.1, 0), color: beast, pos: [-2.2, 0.6, 0.6], scale: [1.7, 0.6, 0.9] },             // dead draft-beast
      { geo: box(1, 1, 1), color: 0x6a5236, pos: [-0.6, 0.5, 1.8], rot: [0, 0.5, 0] },               // spilled crate
      { geo: box(0.9, 0.9, 0.9), color: 0x6a5236, pos: [1.2, 0.45, 2.4], rot: [0.3, 0.2, 0] },
    ], x, z);
    g.rotation.y = p.rot || 0; return g;
  },
  statue(ctx, x, z, p = {}) {                           // a broken colossus, half-toppled
    const st = p.stone || ctx.palette.stone || 0x5a564e, lean = p.lean != null ? p.lean : 0.5;
    const g = add(ctx, [
      { geo: box(6, 2, 6), color: st, pos: [0, 1, 0] },                                             // plinth
      { geo: box(3, 9, 2.4), color: st, pos: [0, 6, 0] },                                           // torso
      { geo: box(4.4, 1.6, 3), color: st, pos: [0, 11, 0] },                                        // shoulders
      { geo: ico(1.4, 0), color: st, pos: [0, 12.6, 0] },                                           // head
      { geo: box(1.1, 4, 1.1), color: st, pos: [3, 2, 1], rot: [0, 0, 1.2] },                       // fallen arm
    ], x, z);
    g.rotation.set(lean * 0.16, p.ry || 0.3, lean * 0.12);
    if (p.solid !== false && ctx.solid) ctx.solid.circle(x, z, 3.4);
    return g;
  },
  waterfall(ctx, x, z, p = {}) {                        // a frozen / white cascade down a cliff face
    const col = p.color || 0xcfe2ee, h = p.h || 30;
    const sheet = new THREE.Mesh(new THREE.PlaneGeometry(p.w || 10, h), mat(col, { op: 0.9, flat: false, side: THREE.DoubleSide }));
    sheet.position.set(x, h / 2, z); sheet.rotation.y = p.ry || 0; ctx.group.add(sheet);
    add(ctx, [{ geo: ico(4, 0), color: col, scale: [1.6, 0.4, 1.2] }], x, z);                       // pool at the base
  },
  fallenLog(ctx, x, z, p = {}) {                        // a great toppled trunk — cover
    const wood = p.wood || 0x3a2e22;
    const g = add(ctx, [
      { geo: cyl(1.2, 1.5, 16, 7), color: wood, pos: [0, 1.3, 0], rot: [0, 0, 1.5708] },
      { geo: cyl(0.3, 0.5, 3, 5), color: wood, pos: [4, 2.2, 0.5], rot: [0.5, 0, 1.2] },
    ], x, z);
    const rot = p.rot != null ? p.rot : Math.random() * 3.14; g.rotation.y = rot;
    if (p.solid !== false && ctx.solid) ctx.solid.box(x, z, 8, 1.4, rot);   // a low log you slide along
    return g;
  },
  ballista(ctx, x, z, p = {}) {
    const wood = 0x4a3826;
    const g = add(ctx, [{ geo: box(1.2, 0.8, 4), color: wood, pos: [0, 1.4, 0] }, { geo: box(5, 0.3, 0.3), color: wood, pos: [0, 1.8, -0.8] }, { geo: box(0.3, 1.4, 0.3), color: wood, pos: [0, 0.7, 0] }, { geo: cyl(0.8, 0.8, 0.3, 8), color: 0x2a2018, pos: [0, 0.6, 1], rot: [1.5708, 0, 0] }], x, z);
    g.rotation.y = p.rot || 0; return g;
  },
  // ---- terrain framing ----
  cliff(ctx, x, z, p = {}) {
    const st = p.stone || ctx.palette.cliff || 0x585552, rot = p.rot || 0, len = p.len || 70, h = p.h || 34, d = p.d || 22;
    const parts = [];
    const n = Math.max(3, Math.round(len / 14));
    for (let i = 0; i < n; i++) {
      const hh = h * (0.6 + Math.random() * 0.6), ww = len / n * (0.9 + Math.random() * 0.4);
      parts.push({ geo: ico(ww, 0), color: st, pos: [(i - (n - 1) / 2) * (len / n), hh * 0.3, (Math.random() - 0.5) * 6], scale: [1, hh / ww, d / ww], rot: [0, Math.random(), 0] });
    }
    const g = add(ctx, parts, x, z); g.rotation.y = rot;
    // cap the solid's long axis so edge-framing cliffs never straddle the map
    // clamp line (a solid past ±(MAP_HALF-2) would fling clamped movers off-field)
    if (p.solid !== false && ctx.solid) ctx.solid.box(x, z, Math.min(len / 2, 186), d / 2, rot);
    return g;
  },
  hill(ctx, x, z, p = {}) {
    const col = p.color || ctx.palette.ground || 0x4a4338, r = p.r || 40;
    const g = add(ctx, [{ geo: ico(r, 1), color: col, scale: [1, (p.h || 12) / r, 1] }], x, z);
    g.position.y = -(r - (p.h || 12));    // sink so only the cap shows
    return g;
  },
  mountain(ctx, x, z, p = {}) {
    const col = p.color || ctx.palette.cliff || 0x4e4b47, r = p.r || 55;
    const g = add(ctx, [
      { geo: new THREE.ConeGeometry(r, (p.h || 80), 6), color: col },
      { geo: new THREE.ConeGeometry(r * 0.5, (p.h || 80) * 0.55, 6), color: p.snow || 0xdfe6ee, pos: [0, (p.h || 80) * 0.35, 0] },
    ], x, z);
    g.position.y = (p.h || 80) / 2 - 6;
    // mountains are skyline framing at the map rim — only register a solid if the
    // whole footprint fits INSIDE the clamp band, else it traps clamped movers
    // on its off-field side (review finding: frost_pass mountain pinned spawns).
    if (p.solid !== false && ctx.solid) {
      const sr = Math.min(r * 0.5, 194 - Math.abs(x), 194 - Math.abs(z));
      if (sr > 3) ctx.solid.circle(x, z, sr);
    }
    return g;
  },
};

// water plane (sea/lake/bog/river) — a big translucent shimmering sheet
export function water(group, { x = 0, z = 0, w = 200, d = 200, color = 0x2a4a5a, y = -0.15, anims }) {
  const geo = new THREE.PlaneGeometry(w, d, 1, 1).rotateX(-Math.PI / 2);
  const m = mat(color, { op: 0.82, flat: false });
  const mesh = new THREE.Mesh(geo, m); mesh.position.set(x, y, z); mesh.renderOrder = -1;
  group.add(mesh);
  if (anims) anims((dt, t) => { mesh.position.y = y + Math.sin(t * 0.8) * 0.06; });
  return mesh;
}
